import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { deleteAllGirlImages, deleteSpecificImage, deleteFirstImage } from '../../utils/imageCleanup';

const ImageCleanup = () => {
  const { currentUser } = useAuth();
  const [isCleaning, setIsCleaning] = useState(false);
  const [cleanupResult, setCleanupResult] = useState(null);

  const handleDeleteAllImages = async () => {
    if (!currentUser) {
      alert('กรุณาล็อกอินก่อน');
      return;
    }

    if (!window.confirm('คุณแน่ใจหรือไม่ที่จะลบรูปภาพทั้งหมด? การกระทำนี้ไม่สามารถย้อนกลับได้!')) {
      return;
    }

    setIsCleaning(true);
    setCleanupResult(null);

    try {
      const success = await deleteAllGirlImages(currentUser.uid);
      setCleanupResult({
        type: 'all',
        success,
        message: success ? 'ลบรูปภาพทั้งหมดสำเร็จ!' : 'เกิดข้อผิดพลาดในการลบรูปภาพทั้งหมด'
      });
    } catch (error) {
      console.error('Error deleting all images:', error);
      setCleanupResult({
        type: 'all',
        success: false,
        message: 'เกิดข้อผิดพลาดในการลบรูปภาพทั้งหมด'
      });
    } finally {
      setIsCleaning(false);
    }
  };

  const handleDeleteFirstImage = async () => {
    if (!currentUser) {
      alert('กรุณาล็อกอินก่อน');
      return;
    }

    if (!window.confirm('คุณแน่ใจหรือไม่ที่จะลบรูปภาพแรก? การกระทำนี้ไม่สามารถย้อนกลับได้!')) {
      return;
    }

    setIsCleaning(true);
    setCleanupResult(null);

    try {
      const success = await deleteFirstImage(currentUser.uid);
      setCleanupResult({
        type: 'first',
        success,
        message: success ? 'ลบรูปภาพแรกสำเร็จ!' : 'ไม่พบรูปภาพแรกหรือเกิดข้อผิดพลาด'
      });
    } catch (error) {
      console.error('Error deleting first image:', error);
      setCleanupResult({
        type: 'first',
        success: false,
        message: 'เกิดข้อผิดพลาดในการลบรูปภาพแรก'
      });
    } finally {
      setIsCleaning(false);
    }
  };

  return (
    <div className="p-6 bg-white rounded-lg shadow-md">
      <h2 className="text-xl font-semibold mb-4">🧹 ทำความสะอาดรูปภาพ</h2>
      <p className="text-gray-600 text-sm mb-4">
        ใช้เครื่องมือนี้เพื่อลบรูปภาพที่เริ่มพัฒนาหรือไม่ต้องการออกจากระบบ
      </p>
      
      <div className="space-y-4">
        {/* ลบรูปภาพแรก */}
        <div className="border border-gray-200 rounded-lg p-4">
          <h3 className="font-semibold text-gray-800 mb-2">ลบรูปภาพแรก (สำหรับการเริ่มพัฒนา)</h3>
          <p className="text-sm text-gray-600 mb-3">
            ลบรูปภาพแรกที่แสดงในรายการ "อนุมัติแล้ว" ออกจากระบบ
          </p>
          <button
            onClick={handleDeleteFirstImage}
            disabled={isCleaning}
            className={`px-4 py-2 rounded-md font-medium transition ${
              isCleaning
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-yellow-500 text-white hover:bg-yellow-600'
            }`}
          >
            {isCleaning ? 'กำลังลบ...' : '🗑️ ลบรูปภาพแรก'}
          </button>
        </div>

        {/* ลบรูปภาพทั้งหมด */}
        <div className="border border-red-200 rounded-lg p-4 bg-red-50">
          <h3 className="font-semibold text-red-800 mb-2">⚠️ ลบรูปภาพทั้งหมด</h3>
          <p className="text-sm text-red-600 mb-3">
            ลบรูปภาพทั้งหมดของน้องคนนี้ (อนุมัติแล้ว, รออนุมัติ, ถูกปฏิเสธ) ออกจากระบบ
          </p>
          <button
            onClick={handleDeleteAllImages}
            disabled={isCleaning}
            className={`px-4 py-2 rounded-md font-medium transition ${
              isCleaning
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-red-500 text-white hover:bg-red-600'
            }`}
          >
            {isCleaning ? 'กำลังลบ...' : '🗑️ ลบรูปภาพทั้งหมด'}
          </button>
        </div>
      </div>

      {/* ผลลัพธ์ */}
      {cleanupResult && (
        <div className={`mt-4 p-4 rounded-lg ${
          cleanupResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
        }`}>
          <h3 className={`font-semibold mb-2 ${
            cleanupResult.success ? 'text-green-800' : 'text-red-800'
          }`}>
            ผลการทำความสะอาด:
          </h3>
          <p className={`text-sm ${
            cleanupResult.success ? 'text-green-700' : 'text-red-700'
          }`}>
            {cleanupResult.message}
          </p>
          {cleanupResult.success && (
            <p className="text-xs text-green-600 mt-2">
              💡 แนะนำ: รีเฟรชหน้าเว็บเพื่อดูการเปลี่ยนแปลง
            </p>
          )}
        </div>
      )}

      {/* คำเตือน */}
      <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
        <h3 className="font-semibold text-yellow-800 mb-2">⚠️ คำเตือน</h3>
        <ul className="text-sm text-yellow-700 space-y-1">
          <li>• การลบรูปภาพไม่สามารถย้อนกลับได้</li>
          <li>• รูปภาพจะถูกลบจากทั้ง Firebase Storage และ Firestore</li>
          <li>• แนะนำให้สำรองข้อมูลก่อนลบ</li>
          <li>• หากไม่แน่ใจ ให้ใช้ปุ่ม "ลบรูปภาพแรก" แทน</li>
        </ul>
      </div>
    </div>
  );
};

export default ImageCleanup;









